/** AppBar control for the console-wide tenant scope.
 *
 * Unset it is a subdued outline button ("All tenants"); set it is a filled chip
 * that reads like the multiple picker's closed field — "Acme Adventures · Both",
 * "Acme Adventures +2" — with a ✕ that clears the scope. The popover holds the
 * same picker, `<TenantPicker multiple>`, labelled Scope.
 *
 * The chip dims (60%) where the scope does nothing: on pages where nothing
 * consumes it (the tour-operator tree, OTAs, the dashboard), and on pages whose
 * consumers take one tenant at a time while the scope names several — the tooltip
 * says which (`useScopeApplication`). The scope is still in effect either way.
 *
 * `compact` (the app bar below `sm`): an icon button either way — filled while a scope is
 * set — whose name and tooltip say the scope; the popover sets and clears it.
 */
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { HelpLink } from '@/components/HelpLink';
import { summarizeSelection } from '@/components/TenantPicker/clients';
import { EnvWord, TenantPicker, useTenantSelection } from '@/components/TenantPicker/TenantPicker';

import { setTenantScopeList, useScopeApplication, useTenantScopeList } from './tenantScope';

export function ScopeSelector({ compact = false }: { compact?: boolean } = {}) {
  const scopeList = useTenantScopeList();
  const application = useScopeApplication();
  // Clients and names for the chip: one batched `?ids=` lookup for what the
  // brief cache does not hold yet.
  const summary = summarizeSelection(useTenantSelection(scopeList));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const set = summary !== null;
  const applies = application === 'applies';
  /** With codes, so similarly named operators stay apart for a screen reader. */
  const described = summary?.fullWithCodes ?? '';
  const tooltip = !summary
    ? 'Scope the whole console to one or more tenants'
    : application === 'single-only'
      ? `This page works on one tenant at a time; the scope names ${scopeList.length}.`
      : applies
        ? `Console scoped to ${summary.full}`
        : `Console scoped to ${summary.full} — this page isn't tenant-scoped`;

  return (
    <>
      <Tooltip title={compact && set ? `Console scope: ${described}` : tooltip}>
        {compact ? (
          <IconButton
            color="inherit"
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label={set ? `Console scope: ${described}` : 'Set console scope'}
            data-scope-compact={set ? 'set' : 'unset'}
            sx={{
              opacity: set && !applies ? 0.6 : set ? 1 : 0.8,
              ...(set ? { bgcolor: 'rgba(255,255,255,0.22)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } } : {}),
            }}
          >
            <BusinessIcon />
          </IconButton>
        ) : summary ? (
          <Chip
            icon={<BusinessIcon />}
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {summary.first.client.name}
                </Box>
                {summary.env && (
                  <Box component="span" sx={{ display: 'inline-flex', fontWeight: 500 }}>
                    <EnvWord sandbox={summary.first.state === 'sandbox'} word={summary.env} />
                  </Box>
                )}
                {summary.more > 0 && (
                  <Box
                    component="span"
                    sx={{
                      flex: 'none',
                      fontSize: 12,
                      lineHeight: '18px',
                      px: 0.875,
                      borderRadius: 999,
                      bgcolor: 'rgba(255,255,255,0.25)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    +{summary.more}
                  </Box>
                )}
              </Box>
            }
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            onDelete={() => setTenantScopeList([])}
            deleteIcon={<CloseIcon aria-label="Clear console scope" />}
            aria-label={`Console scope: ${described}`}
            data-scope-applies={application}
            sx={{
              maxWidth: 300,
              color: 'inherit',
              fontWeight: 600,
              borderColor: 'rgba(255,255,255,0.5)',
              bgcolor: applies ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
              opacity: applies ? 1 : 0.6,
              '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: 'inherit' },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          />
        ) : (
          <Button
            color="inherit"
            size="small"
            startIcon={<BusinessIcon />}
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label="Set console scope"
            sx={{ opacity: 0.8, textTransform: 'none' }}
          >
            All tenants
          </Button>
        )}
      </Tooltip>

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, width: 420 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography variant="subtitle2">Console scope</Typography>
          <HelpLink story="ui-tenant-scope" />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Every tenant-aware page filters to these operators until you clear the scope. A page can
          still be pointed at other tenants, and a saved view that pins a tenant keeps its own. Pages
          that work on one tenant at a time use the scope only when it names one.
        </Typography>
        <TenantPicker
          multiple
          label="Scope"
          placeholder="All tenants"
          fieldWidth="100%"
          value={scopeList}
          onChange={(next) =>
            setTenantScopeList(next.map((t) => ({ id: t.id, name: t.name, ...(t.code ? { code: t.code } : {}) })))
          }
        />
        {set && (
          <Button
            size="small"
            sx={{ mt: 1.5 }}
            onClick={() => {
              setTenantScopeList([]);
              setAnchor(null);
            }}
          >
            Clear scope
          </Button>
        )}
      </Popover>
    </>
  );
}
